import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  // Get email from navigation state or localStorage
  const [email, setEmail] = useState('');

  useEffect(() => {
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem('pendingVerificationEmail');
    
    if (stateEmail) {
      setEmail(stateEmail);
      localStorage.setItem('pendingVerificationEmail', stateEmail);
    } else if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // If no email, redirect to login
      navigate('/login');
    }
  }, [location, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error('Masukkan kode verifikasi');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, {
        email: email,
        code: code.trim()
      });
      
      toast.success('Email berhasil diverifikasi! Silakan login.');
      localStorage.removeItem('pendingVerificationEmail');
      navigate('/login');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Verifikasi gagal';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: email });
      
      toast.success('Kode verifikasi baru telah dikirim ke email Anda');
      setCooldown(120); // 2 minutes cooldown
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string' && errorDetail.includes('tunggu')) {
        // Extract remaining seconds from error message
        const match = errorDetail.match(/(\d+) detik/);
        if (match) {
          setCooldown(parseInt(match[1]));
        }
        toast.error(errorDetail);
      } else {
        toast.error(errorDetail || 'Gagal mengirim ulang kode verifikasi');
      }
    } finally {
      setResending(false);
    }
  };

  const formatCooldown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-ltb-br flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        {/* Back to Login */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/login')}
            className="text-ltb-bright hover:text-ltb-deep flex items-center gap-1 text-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali ke Login
          </button>
        </div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-gradient-ltb-r rounded-full p-4 mb-4">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifikasi Email</h1>
          <p className="text-gray-600 text-sm">
            Masukkan kode verifikasi yang telah dikirim ke
          </p>
          <p className="text-ltb-bright font-semibold mt-1">{email}</p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          {/* Code Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kode Verifikasi
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              className="w-full px-4 py-3 text-center text-2xl tracking-widest font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
              placeholder="XXXXXX"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              Masukkan 6 digit kode dari email Anda
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="w-full bg-gradient-ltb-r text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Memverifikasi...
              </>
            ) : (
              'Verifikasi'
            )}
          </button>
        </form>

        {/* Resend Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-sm text-gray-600 mb-3">
            Tidak menerima kode?
          </p>
          
          {cooldown > 0 ? (
            <div className="text-center">
              <p className="text-gray-500 text-sm">
                Kirim ulang dalam <span className="font-bold text-ltb-bright">{formatCooldown(cooldown)}</span>
              </p>
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              className="w-full py-2 text-ltb-bright font-semibold hover:bg-ltb-bright/10 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {resending ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Mengirim...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Kirim Ulang Kode
                </>
              )}
            </button>
          )}
        </div>

        {/* Help Text */}
        <p className="text-xs text-gray-400 text-center mt-6">
          Periksa folder spam jika tidak menemukan email verifikasi
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
